import first from "lodash/first";
import type { FunctionComponent } from "react";
import { Fragment, useEffect } from "react";
import { graphql, useFragment, useQueryLoader } from "react-relay";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { section as sectionData } from "shared/data";
import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import { useAppBarStickyHeightValue } from "scmp-app/components/app-bar/hooks";
import { Share } from "scmp-app/components/article/article-2.0-action-widget/share";
import type { ArticleRenderChildProps } from "scmp-app/components/article/article-render";
import { ContentItemSeriesPrimary } from "scmp-app/components/content/content-item-render/variants/series-primary";
import type { seriesArticleArticle$key } from "scmp-app/queries/__generated__/seriesArticleArticle.graphql";
import type { seriesArticleQuery } from "scmp-app/queries/__generated__/seriesArticleQuery.graphql";

import {
  ActionWidget,
  BannerAdSlot,
  Container,
  DesktopAdSlot,
  ItemContainer,
  List,
  MobileAndTabletAdSlot,
  Right,
  RightSectionSticky,
  SeriesItemContainer,
  SeriesNumber,
  StyledContentItemSeriesBanner,
  StyledSeriesArticleOtherScmpSeries,
  Wrapper,
} from "./styles";

export type Props = ArticleRenderChildProps & {
  reference: seriesArticleArticle$key;
};

export const query = graphql`
  query seriesArticleQuery($queueName: String!, $excludeEntityIds: [String]) {
    queue(filter: { name: $queueName }) {
      ...otherScmpSeriesSeriesArticleQueue @arguments(excludeEntityIds: $excludeEntityIds)
    }
  }
`;

export const SeriesArticle: FunctionComponent<Props> = ({ provider, reference }) => {
  const [queryReference, loadQuery, disposeQuery] = useQueryLoader<seriesArticleQuery>(query);
  const article = useFragment(
    graphql`
      fragment seriesArticleArticle on Article {
        entityId
        sectionsV2 {
          value {
            ... on Section {
              entityId
            }
          }
        }
        seriesReverseOrder
        series {
          ... on Content {
            ...seriesPrimaryContent
          }
        }
        ...seriesBannerContent
        ...shareArticle20Content
      }
    `,
    reference,
  );
  const { appBarStickyHeight } = useAppBarStickyHeightValue();

  useEffect(() => {
    const articleSectionIds =
      article?.sectionsV2?.flatMap(
        sections => sections?.value?.map(value => value?.entityId ?? "") ?? [],
      ) ?? [];

    // Remove the news, comment, and magazine sections from the array
    const queueId = first(
      articleSectionIds.filter(
        sectionId =>
          !(
            [
              sectionData.news.entityId,
              sectionData.comment.entityId,
              sectionData.magazine.entityId,
            ] as string[]
          ).includes(sectionId),
      ),
    );

    loadQuery({
      excludeEntityIds: [article.entityId],
      queueName: `section_top_series_${queueId}`,
    });

    return disposeQuery;
  }, [article, loadQuery, disposeQuery]);

  const actionWidget = () => (
    <ActionWidget>
      <Share reference={article} />
      {provider.actionWidget({ variant: "series" })}
    </ActionWidget>
  );

  return (
    <Container>
      <Wrapper>
        <BannerAdSlot>
          <MobileAndTabletAdSlot>
            {provider.adslot({
              adUnit: "m_banner3",
              breakpoint: theme.breakpoints.down("desktop"),
              sizes: [
                [300, 50],
                [300, 100],
                [320, 50],
                [320, 100],
              ],
              withBackground: true,
              withLabel: true,
            })}
          </MobileAndTabletAdSlot>
          <DesktopAdSlot>
            {provider.adslot({
              adUnit: "d_banner1",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [
                [970, 250],
                [728, 90],
                [970, 90],
              ],
              withBackground: true,
            })}
          </DesktopAdSlot>
        </BannerAdSlot>
        <StyledContentItemSeriesBanner reference={article} share={actionWidget()} />
        <Right>
          <RightSectionSticky $stickyStackedHeight={appBarStickyHeight}>
            {provider.adslot({
              adUnit: "d_lrec1",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[300, 250]],
            })}
          </RightSectionSticky>
        </Right>
        <List $isReverseOrder={article.seriesReverseOrder ?? false}>
          {article.series?.filter(notEmpty)?.map((series, index) => (
            <Fragment key={index}>
              <ItemContainer>
                <SeriesItemContainer>
                  <SeriesNumber>
                    [<span>{index + 1}</span>]
                  </SeriesNumber>
                  <ContentItemSeriesPrimary
                    key={index}
                    reference={series}
                    topicLinkVariant={{ type: "main" }}
                  />
                </SeriesItemContainer>
              </ItemContainer>
              {(article.seriesReverseOrder
                ? index === (article.series?.length ?? 0) - 2 // If it's reversed order, display in the second last item
                : index === 0) && (
                <ItemContainer $isAds>
                  <MobileAndTabletAdSlot>
                    {provider.adslot({
                      adUnit: "m_lrec1",
                      breakpoint: theme.breakpoints.down("desktop"),
                      sizes: [[300, 250]],
                      withBackground: true,
                      withLabel: true,
                    })}
                  </MobileAndTabletAdSlot>
                  <DesktopAdSlot>
                    {provider.adslot({
                      adUnit: "d_lrec1",
                      breakpoint: theme.breakpoints.up("desktop"),
                      sizes: [[300, 250]],
                    })}
                  </DesktopAdSlot>
                </ItemContainer>
              )}
            </Fragment>
          ))}
        </List>
        <ClientSideSuspense fallback={<></>}>
          {queryReference && <StyledSeriesArticleOtherScmpSeries queryReference={queryReference} />}
        </ClientSideSuspense>
      </Wrapper>
    </Container>
  );
};

SeriesArticle.displayName = "SeriesArticle";
